import React, { useState, useEffect, useMemo } from "react";
import DefaultInput from "../../inputs/defaultInput";
import DefaultSearchSelectInput from "../../inputs/defaultSearchSelectInput";
import DefaultButton from "../../buttons/defaultButton";
import DefaultTextarea from "../../inputs/defaultTextarea";
import { IoMdAdd, IoMdClose } from "react-icons/io";
import { HiOutlineOfficeBuilding, HiBriefcase } from "react-icons/hi";
import { createDepartment, getDepartmentList } from "../../../../services/workspace/department.service";
import { createDesignation, getDesignationList } from "../../../../services/workspace/designation.service";
import { getRoleList } from "../../../../services/workspace/role.service";
import toast from "react-hot-toast";

const DepartmentsStep = ({ value = [], onChange, payrollCycle }) => {
  const [departments, setDepartments] = useState(value.departments || []);
  const [designations, setDesignations] = useState(value.designations || []);

  // Fetch existing departments from backend on mount (only if not already loaded)
  useEffect(() => {
    const fetchExistingDepartments = async () => {
      try {
        const response = await getDepartmentList();
        const deptList = response?.department?.data || response?.data || [];
        
        if (Array.isArray(deptList) && deptList.length > 0) {
          // Transform backend departments to local format
          const transformedDepts = deptList.map((dept) => ({
            id: `dept-existing-${dept.id || dept.uuid}`, // Local ID for dropdown
            savedId: dept.id || dept.uuid, // Backend ID
            name: dept.name || "",
            abrv: dept.abrv || "",
            description: dept.description || "",
            parentId: null, // Will be set if parent_id exists in backend
            managerId: null,
            children: [],
            isCreated: true, // Mark as already created
          }));
          
          // Only add departments that don't already exist in state (by savedId)
          setDepartments((prevDepts) => {
            const existingSavedIds = new Set(
              prevDepts.map((d) => d.savedId).filter(Boolean)
            );
            const newDepts = transformedDepts.filter(
              (d) => d.savedId && !existingSavedIds.has(d.savedId)
            );
            if (newDepts.length > 0) {
              const updated = [...prevDepts, ...newDepts];
              // Notify parent component about the updated departments
              onChange({ departments: updated, designations });
              return updated;
            }
            return prevDepts;
          });
        }
      } catch (error) {
        console.error("Failed to fetch existing departments:", error);
        // Don't show error toast - this is optional for onboarding
      }
    };
    
    // Only fetch if departments state is empty or has no saved departments
    const hasSavedDepartments = departments.some((d) => d.savedId);
    if (!hasSavedDepartments && departments.length === 0) {
      fetchExistingDepartments();
    }
  }, []); // Run only on mount

  // Sync state with value prop when it changes externally
  useEffect(() => {
    if (value.departments) {
      setDepartments(value.departments);
    }
    if (value.designations) {
      setDesignations(value.designations);
    }
  }, [value.departments, value.designations]);
  const [newDept, setNewDept] = useState({ name: "", parentId: null, managerId: null });
  const [newDesignation, setNewDesignation] = useState({ 
    name: "", 
    abrv: "", 
    departmentId: null, 
    description: "" 
  });
  const [savingDept, setSavingDept] = useState(false);
  const [savingDesig, setSavingDesig] = useState(false);

  const handleAddDepartment = async () => {
    if (!newDept.name.trim()) return;

    // Check if department with same name already exists in local state
    const existingDept = departments.find(
      (d) => d.name?.toLowerCase().trim() === newDept.name.trim().toLowerCase()
    );
    if (existingDept) {
      toast.error(`Department "${newDept.name.trim()}" already exists in the list.`);
      return;
    }

    setSavingDept(true);
    try {
      // If parent department is selected, get its saved ID (backend ID, not local ID)
      let parentId = null;
      if (newDept.parentId) {
        const parentDept = departments.find((d) => d.id === newDept.parentId);
        
        if (parentDept) {
          // Only use savedId (backend ID) - never use local ID (d.id)
          if (parentDept.savedId) {
            parentId = parentDept.savedId; // This is the backend ID
          } else {
            // Parent exists but not saved yet - allow creation but don't send parent_id
            parentId = null; // Don't send parent_id if parent isn't saved
          }
        } else {
          toast.error("Parent department not found");
          setSavingDept(false);
          return;
        }
      }

      // First save to backend
      const payload = {
        name: newDept.name.trim(),
        ...(parentId && { parent_id: parentId }), // Only include if parentId exists (backend ID)
        ...(newDept.managerId && { manager_id: newDept.managerId }), // Only include if managerId exists
      };
      
      const savedDept = await createDepartment(payload);
      
      // Extract ID from response - handle different response structures
      // createDepartment returns response.data.data, so savedDept should be the department object
      // Try all possible paths for ID
      let departmentId = null;
      
      if (savedDept) {
        // Direct properties (most common case)
        departmentId = savedDept.id || savedDept.uuid;
        
        // Nested in data
        if (!departmentId && savedDept.data) {
          departmentId = savedDept.data.id || savedDept.data.uuid;
        }
        
        // Nested in department
        if (!departmentId && savedDept.department) {
          departmentId = savedDept.department.id || savedDept.department.uuid;
        }
        
        // Nested in data.department
        if (!departmentId && savedDept.data?.department) {
          departmentId = savedDept.data.department.id || savedDept.data.department.uuid;
        }
      }
      
      // If ID not found, try to fetch from department list as fallback
      if (!departmentId) {
        try {
          // Fetch department list and find the department by name
          const deptListResponse = await getDepartmentList();
          const deptList = deptListResponse?.department?.data || deptListResponse?.data || deptListResponse || [];
          
          if (Array.isArray(deptList)) {
            const foundDept = deptList.find(
              (d) => d.name?.toLowerCase().trim() === newDept.name.trim().toLowerCase()
            );
            
            if (foundDept) {
              departmentId = foundDept.id || foundDept.uuid;
            }
          }
        } catch (fetchError) {
          console.error("Failed to fetch department list for fallback:", fetchError);
        }
      }
      
      // Create department object with saved ID (or null if not found)
      const dept = {
        id: `dept-${Date.now()}`,
        savedId: departmentId || null, // Store backend ID (or null if not found)
        name: newDept.name.trim(),
        parentId: newDept.parentId,
        managerId: newDept.managerId,
        children: [],
        isCreated: true, // Flag to indicate this was already created (even if savedId is null)
      };

      const updated = [...departments, dept];
      setDepartments(updated);
      onChange({ departments: updated, designations });
      setNewDept({ name: "", parentId: null, managerId: null });
      
      // Only show success - department was created successfully
      toast.success(`Department "${dept.name}" created successfully!`);
    } catch (error) {
      console.error("Failed to save department:", error);
      const errorMsg = error?.response?.data?.message || error?.message || "Unknown error";
      toast.error(`Failed to create department: ${errorMsg}`);
    } finally {
      setSavingDept(false);
    }
  };

  const handleRemoveDepartment = (id) => {
    const updated = departments.filter((d) => d.id !== id);
    // Also remove designations linked to this department
    const updatedDesignations = designations.filter((d) => d.departmentId !== id);
    setDepartments(updated);
    setDesignations(updatedDesignations);
    onChange({ departments: updated, designations: updatedDesignations });
  };

  const handleUpdateDepartment = (id, field, value) => {
    const updated = departments.map((d) =>
      d.id === id ? { ...d, [field]: value } : d
    );
    setDepartments(updated);
    onChange({ departments: updated, designations });
  };

  // Designation handlers
  const handleAddDesignation = async () => {
    if (!newDesignation.name.trim() || !newDesignation.departmentId) return;

    // Check if designation with same name already exists in local state for the same department
    const existingDesig = designations.find(
      (d) => d.name?.toLowerCase().trim() === newDesignation.name.trim().toLowerCase() &&
             d.departmentId === newDesignation.departmentId
    );
    if (existingDesig) {
      toast.error(`Designation "${newDesignation.name.trim()}" already exists for this department.`);
      return;
    }

    // Find the department to get its saved ID
    const dept = departments.find((d) => d.id === newDesignation.departmentId);
    if (!dept) {
      toast.error("Department not found");
      return;
    }

    // Check if department has savedId (backend ID)
    let departmentSavedId = dept.savedId;
    
    // If savedId is missing, try to fetch from department list as fallback
    if (!departmentSavedId) {
      try {
        // Fetch department list and find the department by name
        const deptListResponse = await getDepartmentList();
        const deptList = deptListResponse?.department?.data || deptListResponse?.data || deptListResponse || [];
        
        if (Array.isArray(deptList)) {
          const foundDept = deptList.find(
            (d) => d.name?.toLowerCase().trim() === dept.name.trim().toLowerCase()
          );
          
          if (foundDept) {
            departmentSavedId = foundDept.id || foundDept.uuid;
            
            // Update the department in state with the found savedId
            const updatedDepts = departments.map((d) =>
              d.id === dept.id ? { ...d, savedId: departmentSavedId } : d
            );
            setDepartments(updatedDepts);
            onChange({ departments: updatedDepts, designations });
          }
        }
      } catch (fetchError) {
        console.error("Failed to fetch department list for fallback:", fetchError);
      }
      
      // If still no ID, show error
      if (!departmentSavedId) {
        toast.error("Department ID not available. Please refresh the page or try adding the department again.");
        return;
      }
    }

    setSavingDesig(true);
    try {
      // Fetch roles - required field for designation
      let roles = [];
      try {
        const rolesResponse = await getRoleList({});
        const rolesData = rolesResponse?.role?.data || rolesResponse?.data || [];
        // Extract role IDs - roles should be an array of IDs
        roles = Array.isArray(rolesData) ? rolesData.map((role) => role.id || role.uuid).filter(Boolean) : [];
      } catch (rolesError) {
        // If roles fetch fails, we'll still try to create with empty array
        // Backend will return error if roles are truly required
      }

      // Save designation to backend first
      const payload = {
        name: newDesignation.name.trim(),
        abrv: newDesignation.abrv.trim() || newDesignation.name.trim().substring(0, 3).toUpperCase(),
        department_id: departmentSavedId, // Use saved department ID (backend ID)
        description: newDesignation.description.trim() || "",
        roles: roles, // Array of role IDs (required field)
      };

      const savedDesig = await createDesignation(payload);

      // Extract ID from response - handle different response structures
      let designationId = null;
      if (savedDesig) {
        designationId = savedDesig.id || savedDesig.uuid || savedDesig.data?.id || savedDesig.data?.uuid;
      }

      // Create designation object with saved ID
      const designation = {
        id: `desig-${Date.now()}`,
        savedId: designationId || null, // Store backend ID (or null if not found)
        name: newDesignation.name.trim(),
        abrv: payload.abrv,
        departmentId: newDesignation.departmentId,
        description: newDesignation.description.trim(),
        isCreated: true, // Flag to indicate this was already created (even if savedId is null)
      };

      const updated = [...designations, designation];
      setDesignations(updated);
      onChange({ departments, designations: updated });
      setNewDesignation({ name: "", abrv: "", departmentId: null, description: "" });
      toast.success(`Designation "${designation.name}" created successfully!`);
    } catch (error) {
      console.error("Failed to save designation:", error);
      const errorMsg = error?.response?.data?.message || error?.message || "Unknown error";
      const errorData = error?.response?.data;
      
      // Handle "name already taken" error - try to fetch existing designation
      if (errorMsg.includes("already been taken") || errorData?.errors?.name?.includes("already been taken")) {
        try {
          const desigListResponse = await getDesignationList({ department_id: departmentSavedId });
          const desigList = desigListResponse?.designation?.data || desigListResponse?.data || desigListResponse || [];
          
          if (Array.isArray(desigList)) {
            const existingDesig = desigList.find(
              (d) => d.name?.toLowerCase().trim() === newDesignation.name.trim().toLowerCase() &&
                     d.department_id === departmentSavedId
            );
            
            if (existingDesig) {
              const designationId = existingDesig.id || existingDesig.uuid;
              // Add existing designation to the list
              const designation = {
                id: `desig-${Date.now()}`,
                savedId: designationId,
                name: newDesignation.name.trim(),
                abrv: existingDesig.abrv || newDesignation.name.trim().substring(0, 3).toUpperCase(),
                departmentId: newDesignation.departmentId,
                description: existingDesig.description || newDesignation.description.trim(),
                isCreated: true,
              };
              
              const updated = [...designations, designation];
              setDesignations(updated);
              onChange({ departments, designations: updated });
              setNewDesignation({ name: "", abrv: "", departmentId: null, description: "" });
              toast.success(`Designation "${designation.name}" already exists and has been added to the list.`);
              return;
            }
          }
        } catch (fetchError) {
          console.error("Failed to fetch existing designation:", fetchError);
        }
        toast.error(`Designation "${newDesignation.name.trim()}" already exists for this department. Please use a different name.`);
      } else {
        toast.error(`Failed to create designation: ${errorMsg}`);
      }
    } finally {
      setSavingDesig(false);
    }
  };

  const handleRemoveDesignation = (id) => {
    const updated = designations.filter((d) => d.id !== id);
    setDesignations(updated);
    onChange({ departments, designations: updated });
  };

  const handleUpdateDesignation = (id, field, value) => {
    const updated = designations.map((d) =>
      d.id === id ? { ...d, [field]: value } : d
    );
    setDesignations(updated);
    onChange({ departments, designations: updated });
  };

  const parentOptions = useMemo(() => {
    if (!departments || !Array.isArray(departments) || departments.length === 0) {
      return [];
    }
    return departments
      .filter((d) => d && d.name && d.id) // Filter out invalid entries
      .map((d) => ({ value: d.id, label: d.name || "Unnamed Department" }));
  }, [departments]);

  const getParentOptions = () => parentOptions;

  const buildOrgTree = (depts, parentId = null) => {
    return depts
      .filter((d) => d.parentId === parentId)
      .map((dept) => ({
        ...dept,
        children: buildOrgTree(depts, dept.id),
      }));
  };

  const orgTree = buildOrgTree(departments);

  const getDepartmentDesignations = (deptId) => {
    return designations.filter((d) => d.departmentId === deptId);
  };

  const renderTree = (nodes, level = 0) => {
    return (
      <ul className={level > 0 ? "ml-4 mt-3" : ""}>
        {nodes.map((node) => {
          const deptDesignations = getDepartmentDesignations(node.id);
          return (
            <li key={node.id} className="mb-4">
              {/* Department Card */}
              <div className="flex items-start gap-3">
                {/* Connector Line */}
                
                <div className="flex-1 bg-white border border-borderColor rounded-lg shadow-sm overflow-hidden">
                  {/* Department Header */}
                  <div className="flex items-center gap-3 p-3 bg-gradient-to-r from-primary-50 to-primary-100 border-b border-borderColor">
                    <div className="bg-gradient-to-r from-[#1E3A8A] to-[#3B82F6] w-10 h-10 rounded-lg flex items-center justify-center text-white flex-shrink-0">
                      <HiOutlineOfficeBuilding className="text-xl" />
                    </div>
                    <div className="flex-1">
                      <h4 className="text-base font-semibold text-theme">{node.name}</h4>
                      <p className="text-xs text-textLight">
                        {deptDesignations.length} designation{deptDesignations.length !== 1 ? "s" : ""}
                      </p>
                    </div>
                  </div>
                  
                  {/* Designations List */}
                  {deptDesignations.length > 0 && (
                    <div className="p-3">
                      <div className="flex flex-wrap gap-2">
                        {deptDesignations.map((desig) => (
                          <div
                            key={desig.id}
                            className="flex items-center gap-2 px-3 py-1.5 bg-white border border-borderColor rounded-md"
                          >
                            <HiBriefcase className="text-theme text-sm" />
                            <span className="text-sm text-theme font-medium">{desig.name}</span>
                            {desig.abrv && (
                              <span className="text-xs text-textLight bg-primary-100 px-1.5 py-0.5 rounded">
                                {desig.abrv}
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {deptDesignations.length === 0 && (
                    <div className="p-3">
                      <p className="text-xs text-textLight italic">No designations added yet</p>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Child Departments */}
              {node.children.length > 0 && (
                <div className="mt-3 relative">
                  <div className="absolute left-0 top-0 bottom-0 w-0.5 bg-borderColor -ml-4"></div>
                  {renderTree(node.children, level + 1)}
                </div>
              )}
            </li>
          );
        })}
      </ul>
    );
  };

  return (
    <div className="flex flex-col gap-6" role="region" aria-labelledby="departments-heading">
      <div>
        <h2 id="departments-heading" className="text-xl font-semibold text-theme mb-2">
          Organization Structure
        </h2>
        <p className="text-sm text-textLight">
          Set up your organizational hierarchy by adding departments and designations. The structure will be displayed below.
        </p>
      </div>

      {/* Add Department Form */}
      <div className=" rounded-lg p-4 border border-borderColor">
        <h3 className="text-sm font-semibold text-theme mb-3">Add Department</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <DefaultInput
            label="Department Name"
            placeholder="e.g., Engineering, Sales"
            value={newDept.name}
            onChange={(e) => setNewDept({ ...newDept, name: e.target.value })}
            required
          />
          <DefaultSearchSelectInput
            label="Parent Department (Optional)"
            value={
              newDept.parentId
                ? parentOptions.find((o) => o.value === newDept.parentId)
                : null
            }
            onChange={(selected) =>
              setNewDept({ ...newDept, parentId: selected?.value || null })
            }
            options={parentOptions}
            placeholder="Select parent department"
          />
          <div className="flex items-end justify-end">
            <DefaultButton
              variant="default"
              name={savingDept ? "Saving..." : "Add Department"}
              icon={<IoMdAdd />}
              onClick={handleAddDepartment}
              disabled={!newDept.name.trim() || savingDept}
            />
          </div>
        </div>
      </div>

      {/* Departments List */}
      {departments.length > 0 && (
        <div className="flex flex-col gap-4">
          <h3 className="text-sm font-semibold text-theme">Departments ({departments.length})</h3>
          <div className="space-y-2">
            {departments.map((dept) => (
              <div
                key={dept.id}
                className="grid grid-cols-3 items-center gap-3 p-3 bg-white border border-borderColor rounded-md"
              >
                <div className="col-span-1">
                  <DefaultInput
                    value={dept.name}
                    onChange={(e) =>
                      handleUpdateDepartment(dept.id, "name", e.target.value)
                    }
                    classnames="mb-0"
                  />
                </div>
                <div className="col-span-1">
                  <DefaultSearchSelectInput
                    value={
                      dept.parentId
                        ? parentOptions.find((o) => o.value === dept.parentId)
                        : null
                    }
                    onChange={(selected) =>
                      handleUpdateDepartment(
                        dept.id,
                        "parentId",
                        selected?.value || null
                      )
                    }
                    options={parentOptions.filter((o) => o.value !== dept.id)}
                    placeholder="Parent"
                    lebelVertical={false}
                  />
                </div>
                <div className="col-span-1 flex justify-end">
                  <DefaultButton
                    variant="danger"
                    icon={<IoMdClose />}
                    size="icon"
                    onClick={() => handleRemoveDepartment(dept.id)}
                    aria-label={`Remove ${dept.name}`}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Org Hierarchy View */}
      {orgTree.length > 0 && (
        <div className="bg-white rounded-lg border border-borderColor shadow-sm overflow-hidden">
          <div className="bg-gradient-to-r from-[#1E3A8A] to-[#3B82F6] p-4">
            <h3 className="text-lg font-semibold text-white mb-1">
              Organizational Hierarchy
            </h3>
            <p className="text-sm text-white/80">
              Visual representation of your organization structure
            </p>
          </div>
          <div className="p-6 min-h-[300px] relative">
            {orgTree.length > 0 ? (
              <div className="relative">
                {renderTree(orgTree)}
              </div>
            ) : (
              <div className="flex items-center justify-center h-64 text-textLight">
                <p>Add departments to see the hierarchy</p>
              </div>
            )}
          </div>
        </div>
      )}

      {departments.length === 0 && (
        <div className="text-center py-8 text-textLight">
          <p>No departments added yet. Add your first department above.</p>
        </div>
      )}

      {/* Designations Section */}
      <div className="border-t border-borderColor pt-6 mt-6">
        <div>
          <h3 className="text-lg font-semibold text-theme mb-2">Designations</h3>
          <p className="text-sm text-textLight mb-4">
            Create job titles and assign them to departments. These will appear in the hierarchy above.
          </p>
        </div>

        {/* Add Designation Form */}
        <div className=" rounded-lg p-4 border border-borderColor mb-4">
          <h4 className="text-sm font-semibold text-theme mb-3">Add Designation</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <DefaultInput
              label="Designation Name"
              placeholder="e.g., Software Engineer, Sales Manager"
              value={newDesignation.name}
              onChange={(e) => setNewDesignation({ ...newDesignation, name: e.target.value })}
              required
            />
            <DefaultInput
              label="Abbreviation"
              placeholder="e.g., SE, SM"
              value={newDesignation.abrv}
              onChange={(e) => setNewDesignation({ ...newDesignation, abrv: e.target.value })}
              info="Short form of designation (optional)"
            />
            <DefaultSearchSelectInput
              label="Department"
              value={
                newDesignation.departmentId
                  ? parentOptions.find((o) => o.value === newDesignation.departmentId)
                  : null
              }
              onChange={(selected) =>
                setNewDesignation({ ...newDesignation, departmentId: selected?.value || null })
              }
              options={parentOptions}
              placeholder="Select department"
              required
            />
            
          </div>
          <div className="mt-4">
            <DefaultTextarea
              label="Description (Optional)"
              placeholder="Enter designation description..."
              value={newDesignation.description}
              onChange={(e) => setNewDesignation({ ...newDesignation, description: e.target.value })}
            />
          </div>
          <div className="flex mt-4">
              <DefaultButton
                variant="default"
                name={savingDesig ? "Saving..." : "Add Designation"}
                icon={<IoMdAdd />}
                onClick={handleAddDesignation}
                disabled={!newDesignation.name.trim() || !newDesignation.departmentId || savingDesig}
              />
            </div>
        </div>

        {/* Designations List */}
        {designations.length > 0 && (
          <div className="flex flex-col gap-4">
            <h4 className="text-sm font-semibold text-theme">
              Designations ({designations.length})
            </h4>
            <div className="space-y-2">
              {designations.map((desig) => {
                const deptName = departments.find((d) => d.id === desig.departmentId)?.name || "N/A";
                return (
                  <div
                    key={desig.id}
                    className="flex items-center gap-3 p-3 bg-white border border-borderColor rounded-md"
                  >
                    <div className="flex-1 grid grid-cols-1 md:grid-cols-3 gap-3">
                      <DefaultInput
                        value={desig.name}
                        onChange={(e) =>
                          handleUpdateDesignation(desig.id, "name", e.target.value)
                        }
                        classnames="mb-0"
                        placeholder="Designation Name"
                      />
                      <DefaultInput
                        value={desig.abrv}
                        onChange={(e) =>
                          handleUpdateDesignation(desig.id, "abrv", e.target.value)
                        }
                        classnames="mb-0"
                        placeholder="Abbreviation"
                      />
                      <DefaultSearchSelectInput
                        value={
                          desig.departmentId
                            ? parentOptions.find((o) => o.value === desig.departmentId)
                            : null
                        }
                        onChange={(selected) =>
                          handleUpdateDesignation(
                            desig.id,
                            "departmentId",
                            selected?.value || null
                          )
                        }
                        options={parentOptions}
                        placeholder="Department"
                        customWidth="w-full"
                        lebelVertical={false}
                      />
                    </div>
                    <DefaultButton
                      variant="danger"
                      icon={<IoMdClose />}
                      size="icon"
                      onClick={() => handleRemoveDesignation(desig.id)}
                      aria-label={`Remove ${desig.name}`}
                    />
                  </div>
                );
              })}
            </div>
          </div>
        )}


        {designations.length === 0 && departments.length > 0 && (
          <div className="text-center py-4 text-textLight rounded-lg p-4">
            <p>No designations added yet. Add your first designation above.</p>
          </div>
        )}

        {designations.length === 0 && departments.length === 0 && (
          <div className="text-center py-4 text-textLight rounded-lg p-4">
            <p>First add departments, then create designations for them.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default DepartmentsStep;

